<script setup>
import { computed } from 'vue'
import brandenburgLocations from '~/data/brandenburg'
import HeroSection from '~/components/landing/HeroSection.vue'
import FeaturesSection from '~/components/landing/FeaturesSection.vue'
import GallerySection from '~/components/landing/GallerySection.vue'
import PackagesSection from '~/components/landing/PackagesSection.vue'
import FaqSection from '~/components/landing/FaqSection.vue'
import ProcessSection from '~/components/landing/ProcessSection.vue'
import LocationSection from '~/components/landing/LocationSection.vue'
import SiteFooter from '~/components/SiteFooter.vue'
import ContactForm from '~/components/landing/ContactForm.vue'
import { getDistrictEventTypes } from '~/data/districtEventTypes'

const pageTitle = 'Fotobox mieten in Brandenburg'
const pageDescription = 'Professionelle Fotobox für Ihre Veranstaltung in Brandenburg mieten. Verfuegbar an vielen Orten in Brandenburg.'

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogDescription: pageDescription,
  ogImage: 'https://mietfotobox.com/static/assets/hero.webp',
  canonical: 'https://mietfotobox.com/brandenburg/'
})

const districts = computed(() => {
  return Object.entries(brandenburgLocations).map(([key, location]) => ({
    slug: location.district || key,
    name: location.name,
    description: location.description || location.title || `Fotobox mieten in ${location.name}`,
    venues: location.venues?.slice(0, 3) || [],
    eventTypes: getDistrictEventTypes('brandenburg')

  }))
})

const faqItems = [
  {
    question: 'Welche Orte in Brandenburg decken Sie ab?',
    answer: 'Auf dieser Seite finden Sie alle aktuell verfügbaren Orte in Brandenburg.'
  },
  {
    question: 'Wie komme ich von Brandenburg zur passenden Eventseite?',
    answer: 'Wählen Sie zuerst den Ort und danach den Anlass. Von dort gelangen Sie direkt zu Paketen, Anfrageformular und lokalen Informationen.'
  },
  {
    question: 'Ist eine Lieferung nach Brandenburg möglich?',
    answer: 'Ja. Wir planen Lieferung, Aufbau und Abholung passend zu Ihrem Veranstaltungsort in Brandenburg.'
  },
  {
    question: 'Kann ich auch ohne fertige Auswahl direkt anfragen?',
    answer: 'Ja. Über das Anfrageformular können Sie Anlass und Ort auch direkt angeben, wenn Sie sich noch nicht final entschieden haben.'
  }
]

const locationDescription = 'Unsere Fotoboxen stehen für ausgewählte Orte in Brandenburg bereit. Wir begleiten Ihre Veranstaltung von der Planung bis zur Abholung und passen das Paket an Anlass und Location an.'
</script>

<template>
  <div>
    <HeroSection
      title="Fotobox mieten in Brandenburg"
      introText="Professionelle Fotoboxen für Veranstaltungen in Brandenburg. Wählen Sie direkt den passenden Ort und Anlass für Ihre Anfrage."
      heroImage="/static/assets/hero.webp"
    />

    <section id="districts" class="bg-white py-16">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <p class="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary">Brandenburg Übersicht</p>
          <h2 class="heading-2 mb-4">Orte und Eventarten in Brandenburg</h2>
          <p class="mx-auto max-w-3xl text-lg text-gray-600">
            Wählen Sie Ihren Ort in Brandenburg und springen Sie direkt auf die passende Übersichtsseite.
          </p>
        </div>

        <div class="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="district in districts"
            :key="district.slug"
            class="rounded-[2rem] border border-gray-200 bg-gray-50 p-6 shadow-sm transition-shadow hover:shadow-lg"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h3 class="mt-2 text-2xl font-semibold text-gray-900">{{ district.name }}</h3>
              </div>
              <NuxtLink
                :to="`/brandenburg/${district.slug}/`"
                class="rounded-full border border-primary/20 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white"
              >
                Zur Seite
              </NuxtLink>
            </div>

            <p class="mt-4 text-gray-600">{{ district.description }}</p>

            <div class="mt-6">
              <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Eventarten</h4>
              <div class="mt-3 grid gap-3">
                <NuxtLink
                  v-for="eventType in district.eventTypes"
                  :key="eventType.slug"
                  :to="`/brandenburg/${district.slug}/${eventType.slug}/`"
                  class="flex items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <span class="flex items-center gap-3">
                    <span class="text-lg">{{ eventType.icon }}</span>
                    <span>{{ eventType.name }}</span>
                  </span>
                  <span class="text-primary">Ansehen</span>
                </NuxtLink>
              </div>
            </div>

            <div v-if="district.venues.length > 0" class="mt-6">
              <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Beliebte Locations</h4>
              <ul class="mt-3 space-y-2 text-sm text-gray-600">
                <li v-for="venue in district.venues" :key="venue.name">{{ venue.name }}</li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

    <FeaturesSection locationName="Brandenburg" eventType="Veranstaltung" />
    <GallerySection locationName="Brandenburg" />
    <PackagesSection locationName="Brandenburg" />
    <div id="inquiry-form" class="bg-pink-700 px-4"></div>
    <ContactForm />
    <FaqSection :faq-items="faqItems" locationName="Brandenburg" />
    <ProcessSection locationName="Brandenburg" />
    <LocationSection locationName="Brandenburg" eventType="Veranstaltungen" :description="locationDescription" />
    <SiteFooter currentLocation="Brandenburg" />
  </div>
</template>
